import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { Transition } from "@uirouter/react";
import { UIView, useCurrentStateAndParams, useRouter } from "@uirouter/react";
import { resolveOverrideDurationFilterParams } from "app/pages/segments/config/segmentsConfigHelpers";
import { useAutoScrollTop } from "app/custom-hooks/useAutoScrollTop";
import { SegmentsSubNav } from "app/pages/segments/SegmentsSubNav";
import { SegmentsAnalysisPageContainer } from "app/pages/segments/styleComponents";
import { StateResolvedContext } from "./StateResolvedContext";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { ErrorBoundary } from "react-error-boundary";

export const SegmentsAnalysisRootContainer: React.FC<{ shouldLogErrorFallback }> = ({ shouldLogErrorFallback }) => {
    const { state, params } = useCurrentStateAndParams();
    const [resolvedState, setResolvedState] = useState({ isStateReady: false });
    const swNavigator = useMemo(() => {
        return proNavigator;
    }, []);
    const router = useRouter();
    router.transitionService.onStart(
        {
            to: (trans) => {
                return trans?.parent?.name === "digitalsuite_segments-analysis";
            },
        },
        (trans: Transition) => {
            updateDurationFilterParams(trans.to(), trans.params());
        },
    );
    const ref = useAutoScrollTop();
    useEffect(() => {
        updateDurationFilterParams(state, params);
        setResolvedState({ isStateReady: true });
    }, []);

    const updateDurationFilterParams = (state, params) => {
        state.overrideDurationFilterParams = resolveOverrideDurationFilterParams(state, params);
        state.overrideDatepickerPreset = state.overrideDurationFilterParams?.overrideDatepickerPresets;

        if (state.overrideDurationFilterParams) {
            if (state.overrideDurationFilterParams.validDuration !== params.duration) {
                state.overrideDurationFilterParams.skipValidation = false;
                swNavigator.go(
                    state.name,
                    {
                        ...params,
                        duration: state.overrideDurationFilterParams.validDuration,
                        ignoreFilterConsistency: true,
                    },
                    { location: "replace" },
                );
            } else {
                state.overrideDurationFilterParams.skipValidation = true;
            }
        }
    };
    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    shouldLogErrorFallback={shouldLogErrorFallback}
                />
            )}
            resetKeys={[state, params]}
        >
            <div className="sw-layout-module-inner">
                <SegmentsAnalysisPageContainer className="u-flex-row u-flex-column">
                    <StateResolvedContext.Provider value={resolvedState}>
                        <SegmentsSubNav />
                    </StateResolvedContext.Provider>
                    <div
                        ref={ref}
                        className="sw-layout-scrollable-element use-sticky-css-rendering fadeIn"
                    >
                        <UIView />
                    </div>
                </SegmentsAnalysisPageContainer>
            </div>
        </ErrorBoundary>
    );
};
